/**
 * The command palette — every page in the product, from one catalogue.
 *
 * ── Why it does not keep its own list of pages ────────────────────────────────────
 *
 * It used to. Thirteen entries, hand-written here: expenses, nominations, two performance
 * pages, voting, a timesheet. Everything the product has grown since — the whole of Attendance,
 * Leave, Shift Roster, Certifications, the admin screens — was unreachable from a box that
 * offers to search "pages", which is worse than not offering it: the reader concludes the page
 * does not exist.
 *
 * The pages now come from `getFavoritablePages`, the same role-gated catalogue the home page's
 * favourites picker uses. Deliberate on two counts: a page added to the product appears here
 * without anybody remembering to add it, and it appears ONLY for somebody whose role can open
 * it — the catalogue gates every route against the same helper its route guard uses, so a
 * result cannot lead to the access-denied screen.
 *
 * ── What the catalogue leaves out, and what is added back here ────────────────────
 *
 * `getFavoritablePages` is synchronous and role-only by contract, so it omits anything gated by
 * configuration that has to be fetched: the certification queues, whose approver is a person
 * named in Settings. A palette CAN wait for that — it is a component, and the hook already
 * exists — so those entries are appended from `useCertificationApproval`.
 *
 * Home and Voting are added for the reason they are missing there too: they hang off the shell
 * rather than off a module's own tree.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNominationRole } from '../../hooks/useNominationRole';
import { useCertificationApproval } from '../../hooks/useCertificationApproval';
import { getFavoritablePages } from '../../utils/favoritePages';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { Nomination, NominationStatus } from '../../types/nomination';
import './GlobalSearch.css';

/**
 * One mark per module, so a result is placed at a glance.
 *
 * Keyed by the catalogue's own group name. A module whose group is not listed still appears,
 * with the fallback mark, rather than being dropped for want of a picture.
 */
const GROUP_ICONS: Record<string, string> = {
  Expenses: '💰',
  Leave: '🗓️',
  Timesheets: '⏱️',
  'Shift Roster': '🔄',
  Attendance: '🕓',
  'Award Nominations': '⭐',
  Performance: '🎯',
  Certifications: '🎓',
  Administration: '🛡️',
  General: '🏠',
};

interface SearchResult {
  id: string;
  type: 'page' | 'nomination' | 'action';
  icon: string;
  title: string;
  subtitle: string;
  path: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [loading, setLoading] = useState(false);

  // Load nominations once when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);

      // Load nominations for search
      NominationServiceWrapper.getAllNominationsRaw()
        .then(setNominations)
        .catch(() => setNominations([]));
    }
  }, [isOpen]);

  const { nominationRole } = useNominationRole(user);
  const { canViewManagerQueue, canViewAdminQueue, canViewFinanceQueue } = useCertificationApproval();

  /**
   * Every page this person can open, as search results.
   *
   * `group` becomes the subtitle, and that is what makes a MODULE name work as a query:
   * "attendance" matches none of the titles — My Attendance, Raw Punches, Device Sync — but it
   * is the group all of them carry, so the module answers as a whole.
   */
  const pageResults = useMemo<SearchResult[]>(() => {
    const catalogue: SearchResult[] = getFavoritablePages(user, nominationRole).map(page => ({
      id: `p-${page.path}`,
      type: /^(new|apply)\b/i.test(page.label) ? 'action' : 'page',
      icon: GROUP_ICONS[page.group] || '📄',
      title: page.label,
      subtitle: page.group,
      path: page.path,
    }));

    /* Off the shell rather than a module tree, so the catalogue does not carry them. */
    const extras: SearchResult[] = [
      { id: 'p-home', type: 'page', icon: GROUP_ICONS.General, title: 'Home', subtitle: 'General', path: '/home' },
      { id: 'p-voting', type: 'page', icon: GROUP_ICONS['Award Nominations'], title: 'Voting Polls', subtitle: 'Award Nominations', path: '/voting' },
      { id: 'p-chat', type: 'page', icon: '💬', title: 'Ask Creyon', subtitle: 'General', path: '/chat' },
    ];

    /* Certifications: the two everybody has, then the queues the configuration decides. */
    const certifications: SearchResult[] = [
      { id: 'p-cert-reimb', type: 'action', icon: GROUP_ICONS.Certifications, title: 'Apply for Reimbursement', subtitle: 'Certifications', path: '/certifications/reimbursement' },
      { id: 'p-cert-all', type: 'page', icon: GROUP_ICONS.Certifications, title: 'All Certifications', subtitle: 'Certifications', path: '/certifications/all' },
    ];
    if (canViewManagerQueue) {
      certifications.push({ id: 'p-cert-appr', type: 'page', icon: GROUP_ICONS.Certifications, title: 'Certification Approvals', subtitle: 'Certifications', path: '/certifications/approvals' });
    }
    if (canViewAdminQueue) {
      certifications.push({ id: 'p-cert-admin', type: 'page', icon: GROUP_ICONS.Certifications, title: 'Certificate Admin Approval', subtitle: 'Certifications', path: '/certifications/admin-approvals' });
    }
    if (canViewFinanceQueue) {
      certifications.push({ id: 'p-cert-fin', type: 'page', icon: GROUP_ICONS.Certifications, title: 'Finance Reimbursements', subtitle: 'Certifications', path: '/certifications/finance' });
    }

    /* One entry per route. Two catalogues meeting is exactly where a duplicate comes from, and
       a palette listing the same page twice reads as a bug in the search. */
    const seen = new Set<string>();
    return [...extras, ...catalogue, ...certifications].filter(item =>
      seen.has(item.path) ? false : (seen.add(item.path), true)
    );
  }, [user, nominationRole, canViewManagerQueue, canViewAdminQueue, canViewFinanceQueue]);

  // Search logic
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    setLoading(true);
    const q = query.toLowerCase();

    /* Title OR module. "leave" finds every Leave page, "punch" finds Raw Punches, and a
       half-remembered module name still lands somewhere useful. Capped so a one-letter query
       cannot push the record matches below the fold. */
    const pages = pageResults
      .filter(p => p.title.toLowerCase().includes(q) || p.subtitle.toLowerCase().includes(q))
      .slice(0, 8);

    // Search nominations
    const nomResults: SearchResult[] = nominations
      .filter(n =>
        n.nomineeName.toLowerCase().includes(q) ||
        n.nominatorName.toLowerCase().includes(q) ||
        n.awardType.toLowerCase().includes(q) ||
        n.reason.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map(n => ({
        id: `n-${n.id}`,
        type: 'nomination' as const,
        icon: n.status === NominationStatus.PUBLISHED ? '🏆' : '⭐',
        title: `${n.nomineeName} — ${n.awardType}`,
        subtitle: `By ${n.nominatorName} · ${n.status}`,
        path: n.status === NominationStatus.PUBLISHED ? '/nominations/feed' : `/nominations/${n.id}`,
      }));

    setResults([...pages, ...nomResults]);
    setSelectedIndex(0);
    setLoading(false);
  }, [query, nominations, pageResults]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, results.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && results[selectedIndex]) {
        navigate(results[selectedIndex].path);
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, results, selectedIndex, navigate, onClose]);

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
        else onClose(); // parent toggles
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Add blur class to body when search is open (blurs sidebar)
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('global-search-active');
    } else {
      document.body.classList.remove('global-search-active');
    }
    return () => document.body.classList.remove('global-search-active');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = (result: SearchResult) => {
    navigate(result.path);
    onClose();
  };

  return (
    <div className="global-search-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="global-search-container">
        <div className="global-search-input-wrapper">
          <svg className="global-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            className="global-search-input"
            type="text"
            placeholder="Search any page, module or nomination..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="global-search-kbd">ESC</kbd>
        </div>

        {query.trim() && (
          <div className="global-search-results">
            {loading ? (
              <div className="global-search-empty">Searching...</div>
            ) : results.length === 0 ? (
              <div className="global-search-empty">No results for "{query}"</div>
            ) : (
              results.map((result, i) => (
                <button
                  key={result.id}
                  className={`global-search-result ${i === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(i)}
                >
                  <span className="global-search-result-icon">{result.icon}</span>
                  <div className="global-search-result-text">
                    <span className="global-search-result-title">{result.title}</span>
                    <span className="global-search-result-subtitle">{result.subtitle}</span>
                  </div>
                  <span className="global-search-result-type">{result.type}</span>
                </button>
              ))
            )}
          </div>
        )}

        {!query.trim() && (
          <div className="global-search-hints">
            {/* Named by MODULE, because that is what the palette now answers. The old hint also
                promised a colleague search that only ever matched nomination text. */}
            <span>Try "attendance", "leave", "shift", "timesheet", "certification" — or a nominee's name</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
